import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { cssMap } from '@compiled/react';

import { Box, Text } from '@atlaskit/primitives/compiled';
import {
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightMedia,
	SpotlightPrimaryAction,
} from '@atlaskit/spotlight';
import { token } from '@atlaskit/tokens';

import { useIsObjectSidebarShown } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';

import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { ChangesRequestedSpotlightImg } from './assets/ChangesRequestedSpotlightImg.tsx';
import { ApprovedSpotlightImg } from './assets/ApprovedSpotlightImg.tsx';
import { useApprovals } from './ApprovalsContext.tsx';

const MAX_DISMISSED_REVIEW_IDS = 200;

const getDismissedReviewIds = (): string[] => {
	try {
		const stored = confluenceLocalStorageInstance.getItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_APPROVALS_COMPLETION_SPOTLIGHT_DISMISSED,
		);
		if (stored) {
			const parsed = JSON.parse(stored);
			if (Array.isArray(parsed)) {
				return parsed;
			}
		}
	} catch {
		// Ignore parse errors or storage access errors
	}
	return [];
};

const addDismissedReviewId = (reviewId: string): void => {
	try {
		const dismissed = getDismissedReviewIds();
		if (!dismissed.includes(reviewId)) {
			const updated = [...dismissed, reviewId].slice(-MAX_DISMISSED_REVIEW_IDS);
			confluenceLocalStorageInstance.setItem(
				PERSISTED_KEYS_ON_SERVER.PERSISTED_APPROVALS_COMPLETION_SPOTLIGHT_DISMISSED,
				JSON.stringify(updated),
			);
		}
	} catch {
		// Ignore storage write errors
	}
};

const styles = cssMap({
	body: {
		whiteSpace: 'pre-wrap',
		paddingBottom: token('space.100'),
	},
	spotlightTargetContainer: {
		position: 'relative',
		display: 'inline-block',
	},
	// Invisible anchor element positioned at the bottom of the target to align the spotlight
	spotlightTargetAnchor: {
		position: 'absolute',
		bottom: token('space.0'),
		right: '36px',
		width: token('space.0'),
		height: token('space.0'),
		pointerEvents: 'none',
	},
});

export const i18n = defineMessages({
	changesRequestedApprovalHeadline: {
		id: 'approvals.completion-spotlight.denied-headline',
		description:
			'Title of the spotlight card when the page is requires changes by atleast 1 person.',
		defaultMessage: 'Changes requested',
	},
	approvedApprovalHeadline: {
		id: 'approvals.completion-spotlight.approved-headline',
		description: 'Title of the spotlight card when the page is approved by all reviewers.',
		defaultMessage: 'Page is approved',
	},
	changesRequestedApprovalBody: {
		id: 'approvals.completion-spotlight.changes-requested-body',
		description:
			'Description in the spotlight card when the page is requires changes by atleast 1 person.',
		defaultMessage:
			'Everyone has responded, but one or more have requested changes be made before your page can be approved.',
	},
	approvedApprovalBody: {
		id: 'approvals.completion-spotlight.approved-body',
		description: 'Description in the spotlight card when the page is approved by all reviewers.',
		defaultMessage: 'Nice work! Everyone approved your page.',
	},
	dismissButtonLabel: {
		id: 'approvals.completion-spotlight.dismiss-button-label',
		description: 'Label for the dismiss button in the spotlight card.',
		defaultMessage: 'Dismiss',
	},
});

type ApprovalsCompletedSpotlightProps = {
	children: (dismiss: () => void) => React.ReactNode;
};

export const ApprovalsCompletedSpotlight = ({ children }: ApprovalsCompletedSpotlightProps) => {
	const { formatMessage } = useIntl();
	const { approvalRequest, isCurrentUserRequestor } = useApprovals();

	const reviewId = approvalRequest?.review?.reviewId;
	const reviewStatus = approvalRequest?.review?.reviewStatus;
	const isCompleted = reviewStatus === 'APPROVED' || reviewStatus === 'DENIED';
	const isChangesRequested = reviewStatus === 'DENIED';

	const [isDismissed, setIsDismissed] = useState(() =>
		reviewId ? getDismissedReviewIds().includes(reviewId) : false,
	);

	// Reset dismissed state when reviewId changes (new approval cycle)
	useEffect(() => {
		if (reviewId) {
			setIsDismissed(getDismissedReviewIds().includes(reviewId));
		}
	}, [reviewId]);

	const dismiss = useCallback(() => {
		setIsDismissed(true);
		if (reviewId) {
			addDismissedReviewId(reviewId);
		}
	}, [reviewId]);

	const isVisible = isCurrentUserRequestor && isCompleted && !isDismissed;

	// Re-key the PopoverProvider whenever the object sidebar (details panel) opens or closes,
	// which shifts the page content left/right and moves the popup anchor's viewport position.
	// Remounting PopoverProvider forces Popper.js to recalculate from the updated anchor coordinates.
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const [popoverKey, setPopoverKey] = useState(0);

	// Use a ref so the effect below can read the latest isVisible without needing it as a
	// dependency — we only want to remount when the sidebar state changes, not when visibility changes.
	const isVisibleRef = useRef(isVisible);
	isVisibleRef.current = isVisible;

	useEffect(() => {
		if (isVisibleRef.current) {
			setPopoverKey((k) => k + 1);
		}
	}, [isObjectSidebarShown]);

	return (
		<PopoverProvider key={popoverKey}>
			{/* Hacky target container to properly position spotlight given pre-existing ref conflicts in ContentStateButton */}
			<div css={styles.spotlightTargetContainer}>
				<div css={styles.spotlightTargetAnchor}>
					<PopoverTarget>
						<div />
					</PopoverTarget>
				</div>
				{children(dismiss)}
			</div>
			<PopoverContent
				dismiss={dismiss}
				placement="bottom-end"
				isVisible={isVisible}
				shouldDismissOnClickOutside={false}
				strategy="absolute"
			>
				<SpotlightCard testId="spotlight">
					<SpotlightHeader>
						<SpotlightHeadline>
							{formatMessage(
								isChangesRequested
									? i18n.changesRequestedApprovalHeadline
									: i18n.approvedApprovalHeadline,
							)}
						</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightMedia>
						{isChangesRequested ? <ChangesRequestedSpotlightImg /> : <ApprovedSpotlightImg />}
					</SpotlightMedia>
					<SpotlightBody>
						<Box xcss={styles.body}>
							<Text>
								{formatMessage(
									isChangesRequested
										? i18n.changesRequestedApprovalBody
										: i18n.approvedApprovalBody,
								)}
							</Text>
						</Box>
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightActions>
							<SpotlightPrimaryAction onClick={dismiss}>
								<FormattedMessage {...i18n.dismissButtonLabel} />
							</SpotlightPrimaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		</PopoverProvider>
	);
};
